import { Box, HStack, Skeleton, Text, Tooltip } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { fetchFoundationCoverage } from "../api/dataMappingClient";

/**
 * How much of the organisation's wanted structure the frame in view covers.
 *
 * The question a user has mid-session is "am I done?", and until now the only answer arrived in the save
 * dialog — after they had left the chat. This is that answer, in the place the work happens.
 *
 * **The number is the save's number, not a second opinion.** It is computed server-side with the same
 * two-pass rule Java applies at promotion, over the whole frame rather than the page the panel is showing —
 * `fetchDataFrameData` caps at 500 rows, so counting here would describe a page.
 *
 * Refetched on the frame's FINGERPRINT, not its row and column counts: the commonest transform in this tool
 * rewrites cells and leaves the shape identical, so a shape-keyed refresh would sit still through exactly
 * the changes that move these numbers.
 *
 * Renders nothing at all when the organisation has no structure (`wanted === 0`) — a strip reading "0 of 0"
 * is a row of furniture that teaches the reader to ignore the one that matters.
 */
export function FoundationCoverageStrip({ sessionId, dfKey, fingerprint }) {
  const [coverage, setCoverage] = useState(null);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (!sessionId || !dfKey) {
      setCoverage(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchFoundationCoverage(sessionId, dfKey)
      .then((data) => {
        if (!cancelled) setCoverage(data);
      })
      .catch((err) => {
        // Never a blocking failure: the save still reports the authoritative count, so a strip that cannot
        // load is a missing convenience rather than a broken session.
        console.warn("could not read foundation coverage", err);
        if (!cancelled) setCoverage(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, dfKey, fingerprint]);

  if (isLoading && !coverage) {
    return <Skeleton height="28px" borderRadius="md" mb={2} />;
  }
  if (!coverage || !coverage.wanted) return null;

  const { wanted, filled, off_schema: offSchema, unit_mismatches: mismatches, missing } = coverage;
  const complete = filled >= wanted && offSchema === 0;

  return (
    <Box
      borderWidth="1px"
      borderColor={complete ? "green.200" : "gray.200"}
      bg={complete ? "green.50" : "gray.50"}
      borderRadius="md"
      px={3}
      py={2}
      mb={2}
    >
      <HStack spacing={3} wrap="wrap" fontSize="body.xs">
        <Tooltip
          label={
            missing?.length
              ? `Still missing: ${missing.slice(0, 12).join(", ")}${
                  missing.length > 12 ? `, and ${missing.length - 12} more` : ""
                }`
              : "Every datapoint this organisation asked for has a figure."
          }
          openDelay={300}
        >
          <Text fontWeight="600" color={complete ? "green.800" : "gray.700"}>
            {filled} of {wanted} wanted datapoints filled
          </Text>
        </Tooltip>

        {/* Only when there are any. The same rule the save panel's own off-schema block follows: a count of
            zero, shown, is a thing the reader learns to scroll past. */}
        {offSchema > 0 ? (
          <Tooltip
            label="These are kept unless you choose to drop them when you save. The list says what the organisation is looking for, not what it is allowed to have."
            openDelay={300}
          >
            <Text color="orange.700">
              · {offSchema} figure{offSchema === 1 ? "" : "s"} match none
            </Text>
          </Tooltip>
        ) : null}

        {mismatches?.length > 0 ? (
          <Tooltip
            label={mismatches
              .slice(0, 8)
              .map((m) => `${m.key}: ${m.actual} — expected ${m.expected}`)
              .join("\n")}
            openDelay={300}
          >
            <Text color="orange.700">
              · {mismatches.length} unit mismatch{mismatches.length === 1 ? "" : "es"}
            </Text>
          </Tooltip>
        ) : null}
      </HStack>
    </Box>
  );
}

export default FoundationCoverageStrip;
